import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCatalogImportsQuery } from '@/api/queries/catalogImports';
import {
    useFinishOnboardingMutation,
    useOrganizationQuery,
} from '@/api/queries/organizations';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useCatalogImport } from '@/pages/catalogImport/store';
import { PATH } from '@/router/PATH';
import { useIsNarrowViewport } from '@/utils';
import {
    DONE_IMPORTS_PAGE_SIZE,
    doneImportsOf,
    type DoneImports,
} from './doneImports';

export type UseDoneStepResult = {
    /** The imports so far, grouped by branch; `null` until the history loads. */
    imports: DoneImports | null;
    isImportsLoading: boolean;
    /** The history could not be read, and no earlier read is on screen. */
    isImportsError: boolean;
    /** Reads the history again after a failure. */
    onRetryImports: () => void;
    /** A phone (below `sm`): the lists keep two columns and the totals stack. */
    isNarrow: boolean;
    /**
     * Import more: another branch's file, from the Branch step; from
     * Accounting first while the organisation still needs it (after Start
     * fresh it has not run). The store keeps only the branch, so the next
     * file starts on an empty Upload, never on the last import's file and
     * record.
     */
    onImportMore: () => void;
    isConfirmOpen: boolean;
    /** Finish: asks first, since finishing closes the wizard for good. */
    onFinish: () => void;
    /** Closes the confirm and forgets a failed attempt; ignored mid-call. */
    onCancelFinish: () => void;
    /** Confirm (and Retry after a failure): calls finish. */
    onConfirmFinish: () => void;
    isFinishing: boolean;
    /** The failed finish, shown in the dialog until Retry or Cancel. */
    finishError: ErrorResponse | null;
};

/**
 * Done's list and actions.
 *
 * The list is every branch's history in one read (`GET catalog-imports`
 * without `branchId`, which the API allows an OrgAdmin, the only role the
 * wizard admits), grouped by `doneImportsOf`. An import, and its undo,
 * invalidate that read, so Done shows the latest after either.
 *
 * Confirming Finish calls `finish`; its mutation settles
 * only after the organisation has been re-read as onboarded, so by the time
 * this navigates home the gate opens the shell. The import store is reset
 * first, so the leave guard has nothing left to ask about.
 */
export const useDoneStep = (): UseDoneStepResult => {
    const navigate = useNavigate();
    const { actions } = useCatalogImport();
    const isNarrow = useIsNarrowViewport();
    // Welcome's rule for Bring my catalog: the gate's cached read.
    const accountingNeeded =
        useOrganizationQuery().data?.accountingStartNeeded ?? false;
    const importsQuery = useCatalogImportsQuery(
        null,
        1,
        DONE_IMPORTS_PAGE_SIZE,
    );
    const page = importsQuery.data;
    const imports = React.useMemo(
        () => (page ? doneImportsOf(page) : null),
        [page],
    );
    const { refetch: refetchImports } = importsQuery;
    const onRetryImports = React.useCallback(() => {
        void refetchImports();
    }, [refetchImports]);
    const finishMutation = useFinishOnboardingMutation();
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

    // Whatever import the store still holds is finished with; the branch
    // stays, as Import another file keeps it. Done's list is the server's
    // history, so it is unaffected.
    const onImportMore = React.useCallback(() => {
        navigate(
            PATH.ONBOARDING_STEP.BIND(
                accountingNeeded ? 'accounting' : 'branch',
            ),
        );
        actions.resetImport();
    }, [accountingNeeded, actions, navigate]);

    const onFinish = React.useCallback(() => setIsConfirmOpen(true), []);

    const { isPending, reset: resetFinish } = finishMutation;
    const onCancelFinish = React.useCallback(() => {
        if (isPending) {
            return;
        }
        resetFinish();
        setIsConfirmOpen(false);
    }, [isPending, resetFinish]);

    const { mutate: finish } = finishMutation;
    const onConfirmFinish = React.useCallback(() => {
        finish(undefined, {
            onSuccess: () => {
                actions.reset();
                navigate(PATH.HOME, { replace: true });
            },
        });
    }, [actions, finish, navigate]);

    return {
        imports,
        // A failed refetch keeps the list it had: only a read that never
        // landed is an error, and Retry shows loading again while it runs.
        isImportsLoading:
            !page && (importsQuery.isPending || importsQuery.isFetching),
        isImportsError:
            !page && importsQuery.isError && !importsQuery.isFetching,
        onRetryImports,
        isNarrow,
        onImportMore,
        isConfirmOpen,
        onFinish,
        onCancelFinish,
        onConfirmFinish,
        isFinishing: isPending,
        finishError: finishMutation.error ?? null,
    };
};
